let test_query_overload_failure = false;
// The name of the WebGL extension
const extension_name = 'EXT_disjoint_timer_query_webgl2';
/**
 * A helper class to create and manage GPU timer queries.
 * Since GPU interfacing is truly asynchronous (not javascript async), the usage might be different than you'd expect.
 * Usage:
 *  - start_timer()
 *  - [do GPU work...]
 *  - stop_timer()
 *  - [continue normal operations, make sure to render frames]
 *  - if (has_result()) {
 *      get_result()
 *      clear_result()
 *    }
 */
export class WebGLTimerQuery {
    static is_supported(gl) {
        return gl.getExtension(extension_name) !== null;
    }
    constructor(name, gl) {
        this._query = null;
        this._extension = null;
        this._gl = gl;
        this.name = name;
        this._extension = gl.getExtension(extension_name);
        if (!this._extension) {
            console.warn('WebGL2 timer query extension not available');
            return;
        }
        WebGLTimerQuery.overload_gl_timer_queries(this._gl, this._extension);
    }
    start_timer() {
        if (!this._extension) {
            console.error('Timer query not supported.');
            return;
        }
        if (this._query) {
            console.error('Timer query already created. Did you forget to call clear_result?');
        }
        this._query = this._gl.createQuery();
        if (!this._query) {
            console.error('Failed to create timer query');
            return;
        }
        // TS ignore here because we've added a (optional) parameter to this function.
        // @ts-ignore
        this._gl.beginQuery(this._extension.TIME_ELAPSED_EXT, this._query, this);
    }
    // Stops the timer. The result will be available later when the frame in flight has finished rendering.
    // See has_result and get_result
    stop_timer() {
        if (!this._query) {
            console.error('Timer query not created.');
            return;
        }
        // TS ignore here because we've added a (optional) parameter to this function.
        // 'this._extension' won't be null as it was required to create 'this._query'
        // @ts-ignore
        this._gl.endQuery(this._extension.TIME_ELAPSED_EXT, this);
    }
    // Check if we have a result to fetch.
    has_result() {
        if (!this._query) {
            console.error('Timer query not created');
            return false;
        }
        const available = this._gl.getQueryParameter(this._query, this._gl.QUERY_RESULT_AVAILABLE);
        return !!available;
    }
    // Returns the elapsed time in nanoseconds
    get_result() {
        if (!this._query) {
            console.error('Timer query not created');
            return null;
        }
        const time = this._gl.getQueryParameter(this._query, this._gl.QUERY_RESULT);
        if (typeof time === 'number') {
            return time;
        }
        else {
            console.error('Failed to fetch elapsed time.');
            return null;
        }
    }
    // Clears the result and deletes the query.
    clear_result() {
        if (this._query) {
            this._gl.deleteQuery(this._query);
            this._query = null;
        }
    }
    dispose() {
        this.clear_result();
    }
    // Disables anyone else from doing timer queries in any gl context
    static disable_external_timer_queries() {
        WebGLTimerQuery._block_external_timer_queries = true;
    }
    // Allows anyone to do timer queries in any gl context
    static enable_external_timer_queries() {
        WebGLTimerQuery._block_external_timer_queries = false;
    }
    // Ugly code to overload functions for beginning and ending WebGL queries, to keep track of state.
    // Allows us to block others (Babylon) from doing timer queries, to prioritize our own.
    // Also gives informational error messages.
    static overload_gl_timer_queries(_gl, _extension) {
        // Store to say that we've already overloaded these _query functions.
        // @ts-ignore
        if (_gl._viewer_overloaded_queries === true) {
            return;
        }
        // @ts-ignore
        _gl._viewer_overloaded_queries = true;
        // Store the current active timer _query
        let active_query = null;
        const original_begin_query_func = _gl.beginQuery.bind(_gl);
        const original_end_query_func = _gl.endQuery.bind(_gl);
        // Helper function for error messages.
        const get_active_query_name = () => {
            if (active_query === null) {
                return 'null';
            }
            else if (active_query === 'external') {
                return 'external';
            }
            else {
                return active_query.name;
            }
        };
        const new_begin = (target, _query, viewer_timer_query) => {
            // We only care about the TIME_ELAPSED _query.
            if (target === _extension.TIME_ELAPSED_EXT) {
                if (WebGLTimerQuery._block_external_timer_queries && viewer_timer_query === undefined) {
                    // Blocked external _query!
                    return;
                }
                // Make sure we have no active _query already running.
                if (active_query !== null) {
                    console.error('Trying to begin query while another is running! Attempted _query: ' + ((viewer_timer_query === null || viewer_timer_query === void 0 ? void 0 : viewer_timer_query.name) || 'external') + ', already running query: ' + get_active_query_name() + '. Blocking query begin.');
                    test_query_overload_failure = true;
                    return;
                }
                active_query = viewer_timer_query !== null && viewer_timer_query !== void 0 ? viewer_timer_query : 'external';
            }
            // Pass-through to the original Web_gl function
            original_begin_query_func(target, _query);
        };
        const new_end = (target, viewer_timer_query) => {
            if (target === _extension.TIME_ELAPSED_EXT) {
                if (WebGLTimerQuery._block_external_timer_queries && viewer_timer_query === undefined) {
                    // The begin should have been blocked, so no need to end.
                    // But failsafe, in case someone blocked whilst an external _query was running.
                    if (active_query === 'external') {
                        console.error('Tracked query is not null. Did you disable external timer queries while one was running? Failsafe and ending the query, expect undefined timer behaviour/results.');
                        test_query_overload_failure = true;
                    }
                    else {
                        return;
                    }
                }
                if (active_query === null) {
                    // This shouldn't happen
                    console.error('Tracked active query was null, did you enable external queries while an external query was running? Or did you double-end the query? Attempted query: ' + ((viewer_timer_query === null || viewer_timer_query === void 0 ? void 0 : viewer_timer_query.name) || 'external') + '. Blocking query end.');
                    test_query_overload_failure = true;
                    return;
                }
                else if (active_query !== 'external') {
                    if (active_query !== viewer_timer_query) {
                        console.error('Timer query collision. Trying to end query ' + (viewer_timer_query === null || viewer_timer_query === void 0 ? void 0 : viewer_timer_query.name) + ', but another is active: ' + active_query.name + '\nBlocking query end.');
                        test_query_overload_failure = true;
                        return;
                    }
                }
                else if (viewer_timer_query !== undefined) {
                    console.error('Trying to end viewer query: ' + viewer_timer_query.name + ', while an external query is running. Blocking query end.');
                    test_query_overload_failure = true;
                    return;
                }
                active_query = null;
            }
            original_end_query_func(target);
        };
        _gl.beginQuery = new_begin.bind(_gl);
        _gl.endQuery = new_end.bind(_gl);
    }
}
WebGLTimerQuery._block_external_timer_queries = false;
// Super ugly testing for error checking on our custom _glBegin_query, _glEnd_query function overwrites.ƒ
// The console will spit out informational errors, but shouldn't say any Test failed: ...
export function test_web_gl_timer_queries(_gl) {
    const _extension = _gl.getExtension(extension_name);
    if (!_extension) {
        console.error("Can't test queries, extension is not supported.");
        return;
    }
    let test = 'No test';
    const check_error = (expected) => {
        if (expected !== test_query_overload_failure) {
            console.error('Test failed: ' + test);
        }
        test = 'Unnamed test';
        test_query_overload_failure = false;
    };
    // Test block 1: No blocking of external queries
    WebGLTimerQuery.enable_external_timer_queries();
    const main_timer = new WebGLTimerQuery('primary', _gl);
    const secondary_timer = new WebGLTimerQuery('secondary', _gl);
    check_error(false);
    console.info('Running tests');
    // Test 1: Just a normal timer query
    main_timer.start_timer();
    check_error(false);
    main_timer.stop_timer();
    check_error(false);
    main_timer.clear_result();
    secondary_timer.clear_result();
    // Test 2: Run overlapping viewer queries
    main_timer.start_timer();
    check_error(false);
    secondary_timer.start_timer();
    check_error(true);
    main_timer.stop_timer();
    check_error(false);
    secondary_timer.stop_timer();
    check_error(true);
    main_timer.clear_result();
    secondary_timer.clear_result();
    // Test 3: Run external query inside internal query
    main_timer.start_timer();
    check_error(false);
    let external_query = _gl.createQuery();
    _gl.beginQuery(_extension.TIME_ELAPSED_EXT, external_query);
    check_error(true);
    main_timer.stop_timer();
    check_error(false);
    _gl.endQuery(_extension.TIME_ELAPSED_EXT);
    check_error(true);
    _gl.deleteQuery(external_query);
    main_timer.clear_result();
    secondary_timer.clear_result();
    // Test 4: Internal query inside external query
    external_query = _gl.createQuery();
    _gl.beginQuery(_extension.TIME_ELAPSED_EXT, external_query);
    check_error(false);
    main_timer.start_timer();
    check_error(true);
    main_timer.stop_timer();
    check_error(true);
    _gl.endQuery(_extension.TIME_ELAPSED_EXT);
    check_error(false);
    _gl.deleteQuery(external_query);
    main_timer.clear_result();
    secondary_timer.clear_result();
    // Test block 2: Block external queries
    WebGLTimerQuery.disable_external_timer_queries();
    // Test 1: Just a normal timer query
    main_timer.start_timer();
    check_error(false);
    main_timer.stop_timer();
    check_error(false);
    main_timer.clear_result();
    secondary_timer.clear_result();
    // Test 2: Run overlapping viewer queries
    main_timer.start_timer();
    check_error(false);
    secondary_timer.start_timer();
    check_error(true);
    main_timer.stop_timer();
    check_error(false);
    secondary_timer.stop_timer();
    check_error(true);
    main_timer.clear_result();
    secondary_timer.clear_result();
    // Test 3: Run external query inside internal query
    main_timer.start_timer();
    check_error(false);
    external_query = _gl.createQuery();
    _gl.beginQuery(_extension.TIME_ELAPSED_EXT, external_query);
    check_error(false);
    main_timer.stop_timer();
    check_error(false);
    _gl.endQuery(_extension.TIME_ELAPSED_EXT);
    check_error(false);
    _gl.deleteQuery(external_query);
    main_timer.clear_result();
    secondary_timer.clear_result();
    // Test 4: Internal query inside external query
    external_query = _gl.createQuery();
    _gl.beginQuery(_extension.TIME_ELAPSED_EXT, external_query);
    check_error(false);
    main_timer.start_timer();
    check_error(false);
    main_timer.stop_timer();
    check_error(false);
    _gl.endQuery(_extension.TIME_ELAPSED_EXT);
    check_error(false);
    _gl.deleteQuery(external_query);
    console.info('Tests finished.');
}
//# sourceMappingURL=WebGLTimerQuery.js.map